import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardLayout";
import { BackButton } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError, fmtSASTDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, FileCheck, CheckCircle2, Clock, XCircle, ShieldCheck } from "lucide-react";

const STATUS_STYLES = {
  approved: { label: "Approved", icon: CheckCircle2, color: "text-[#00C853] bg-[#00C853]/10 border-[#00C853]/30" },
  pending: { label: "Pending review", icon: Clock, color: "text-orange-700 bg-orange-50 border-orange-200" },
  rejected: { label: "Rejected — please resubmit", icon: XCircle, color: "text-red-700 bg-red-50 border-red-200" },
};

// kind -> label, accepted by both individual and business flows
const DOC_DEFS = {
  id_copy: { label: "ID copy", desc: "Clear photo or scan of your South African ID" },
  proof_of_address: { label: "Proof of address", desc: "Utility bill / bank statement (≤ 3 months old)" },
  business_registration: { label: "Certificate of incorporation", desc: "CIPC-issued company registration certificate" },
  director_id: { label: "Director ID copy", desc: "South African ID copy of the listed director" },
};

const REQUIRED = {
  individual: ["id_copy", "proof_of_address"],
  business: ["business_registration", "director_id", "proof_of_address"],
};

export default function MerchantKYC() {
  const { merchant, refresh } = useAuth();
  const [form, setForm] = useState({
    full_name: "", id_number: "", residential_address: "", cellphone: "",
    business_name: "", cipc_number: "", director_id_number: "",
  });
  // docs: { [kind]: {id, filename, size, content_type} }
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // currently uploading kind

  useEffect(() => {
    api.get("/kyc/status").then(({ data }) => {
      if (data) setForm({
        full_name: data.full_name || "",
        id_number: data.id_number || "",
        residential_address: data.residential_address || "",
        cellphone: data.cellphone || "",
        business_name: data.business_name || "",
        cipc_number: data.cipc_number || "",
        director_id_number: data.director_id_number || "",
      });
    }).catch(() => {});
  }, []);

  const accountType = merchant?.account_type || "individual";
  const required = REQUIRED[accountType] || REQUIRED.individual;
  const status = merchant?.kyc_status || "pending";
  const submittedAt = merchant?.kyc_submitted_at;
  const reviewedAt = merchant?.kyc_reviewed_at;
  const ss = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const StatusIcon = ss.icon;

  // Pending review state = KYC was submitted and is awaiting admin decision.
  const isUnderReview = status === "pending" && !!submittedAt;
  // Approved = lock down the page entirely.
  const isApproved = status === "approved";
  // Form is editable in initial pending (no submit yet) and after rejection.
  const isEditable = !isApproved && !isUnderReview;

  const uploadDoc = async (e, kind) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", `kyc_${kind}`);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => ({ ...d, [kind]: data }));
      toast.success(`Uploaded ${DOC_DEFS[kind].label}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const missingDocs = required.filter((k) => !docs[k]);
  const missingFormFields = !form.full_name || !form.id_number || !form.residential_address || !form.cellphone
    || (accountType === "business" && (!form.business_name || !form.cipc_number || !form.director_id_number));
  const canSubmit = isEditable && missingDocs.length === 0 && !missingFormFields && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (missingDocs.length > 0) {
      toast.error(
        "Please upload the missing document(s): " +
          missingDocs.map((k) => DOC_DEFS[k].label).join(", ")
      );
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, document_ids: Object.values(docs).map((f) => f.id) };
      await api.post("/kyc/submit", payload);
      toast.success("KYC submitted — admin will review shortly.");
      setDocs({});
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardShell role="merchant">
      <BackButton to="/dashboard" />
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">KYC verification</p>
          <h1 className="font-display mt-2 text-3xl font-bold text-[#0A2540]">
            {isApproved ? "Your account is verified" : "Verify your identity"}
          </h1>
        </div>
        <Badge data-testid="kyc-status-badge" className={`px-3 py-1.5 border ${ss.color}`}>
          <StatusIcon size={14} className="mr-1.5" />{ss.label}
        </Badge>
      </div>

      {/* APPROVED state — celebrate + lock */}
      {isApproved && (
        <div data-testid="kyc-approved-card" className="bg-white border border-[#00C853]/30 rounded-2xl p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#00C853]/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="text-[#00C853]" size={26} />
            </div>
            <div>
              <p className="font-display text-xl font-semibold text-[#0A2540]">
                You're verified and ready to issue vouchers.
              </p>
              <p className="text-sm text-[#666666] mt-2 leading-relaxed">
                Your KYC was approved {reviewedAt ? `on ${fmtSASTDateTime(reviewedAt)}` : ""}. There's nothing more to submit — you can go straight to topping up your wallet and generating vouchers.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3 max-w-md">
                <a href="/dashboard/wallet" data-testid="kyc-go-wallet" className="bg-[#0A2540] hover:bg-[#133D6B] text-white rounded-lg px-4 py-3 text-sm font-semibold text-center transition-colors">Top up wallet →</a>
                <a href="/dashboard/vouchers" data-testid="kyc-go-vouchers" className="bg-[#00C4B4] hover:bg-[#009E91] text-white rounded-lg px-4 py-3 text-sm font-semibold text-center transition-colors">Issue voucher →</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PENDING REVIEW state — friendly waiting screen, NO submit button */}
      {isUnderReview && (
        <div data-testid="kyc-pending-card" className="bg-orange-50 border border-orange-200 rounded-2xl p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
              <Clock className="text-orange-600" size={24} />
            </div>
            <div>
              <p className="font-display text-xl font-semibold text-[#0A2540]">
                Your submission is being reviewed.
              </p>
              <p className="text-sm text-[#666666] mt-2 leading-relaxed">
                Submitted {fmtSASTDateTime(submittedAt)}. The Praxio team typically reviews within one business day. You'll receive an SMS once a decision is made — no further action is required from you right now.
              </p>
              <p className="text-xs text-[#999999] mt-3">
                Need to update something on your submission? Contact <a className="text-[#00C4B4] font-semibold hover:underline" href="mailto:info@praxio.co.za">info@praxio.co.za</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rejection note (visible above resubmission form) */}
      {status === "rejected" && merchant?.kyc_notes && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <p className="text-sm font-semibold text-[#0A2540]">Reason from admin</p>
          <p className="text-sm text-[#666666] mt-1">{merchant.kyc_notes}</p>
        </div>
      )}

      {/* EDITABLE form */}
      {isEditable && (
        <form onSubmit={submit} data-testid="kyc-form" className="bg-white border border-[#E5E7EB] rounded-2xl p-7 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="fn">Full name</Label><Input data-testid="kyc-fullname-input" id="fn" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="idn">SA ID Number</Label><Input data-testid="kyc-id-number-input" id="idn" required value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} /></div>
            <div className="space-y-2 sm:col-span-2"><Label htmlFor="addr">Residential address</Label><Textarea data-testid="kyc-address-input" id="addr" required rows={3} value={form.residential_address} onChange={(e) => setForm({ ...form, residential_address: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="cell">SA Cellphone</Label><Input data-testid="kyc-cellphone-input" id="cell" required value={form.cellphone} onChange={(e) => setForm({ ...form, cellphone: e.target.value })} placeholder="082 123 4567" /></div>
          </div>

          {accountType === "business" && (
            <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[#E5E7EB]">
              <div className="space-y-2 sm:col-span-2"><Label htmlFor="bn">Business name</Label><Input data-testid="kyc-business-name-input" id="bn" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="cipc">CIPC Registration #</Label><Input data-testid="kyc-cipc-input" id="cipc" required value={form.cipc_number} onChange={(e) => setForm({ ...form, cipc_number: e.target.value })} placeholder="2022/123456/07" /></div>
              <div className="space-y-2"><Label htmlFor="did">Director SA ID #</Label><Input data-testid="kyc-director-id-input" id="did" required value={form.director_id_number} onChange={(e) => setForm({ ...form, director_id_number: e.target.value })} /></div>
            </div>
          )}

          {/* DOCUMENT UPLOADS — one labelled box per required doc */}
          <div className="pt-2 border-t border-[#E5E7EB]">
            <Label>Required documents</Label>
            <p className="text-xs text-[#666666] mt-1">PDF, JPEG or PNG · max 5MB each.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="kyc-doc-uploaders">
              {required.map((kind) => {
                const def = DOC_DEFS[kind];
                const doc = docs[kind];
                const inputId = `kyc-upload-${kind}`;
                return (
                  <div key={kind} className={`border-2 border-dashed rounded-xl p-4 transition-colors ${doc ? "border-[#00C853] bg-[#00C853]/5" : "border-[#E5E7EB] hover:border-[#00C4B4]"}`} data-testid={`kyc-doc-${kind}`}>
                    <p className="font-display text-sm font-semibold text-[#0A2540]">{def.label}</p>
                    <p className="text-[11px] text-[#666666] mt-0.5">{def.desc}</p>
                    {doc ? (
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <FileCheck size={16} className="text-[#00C853] shrink-0" />
                        <span className="truncate text-[#0A2540] font-medium">{doc.filename}</span>
                        <button type="button" onClick={() => setDocs((d) => { const n = {...d}; delete n[kind]; return n; })} className="ml-auto text-xs text-[#666666] hover:text-red-600" data-testid={`kyc-remove-${kind}`}>Replace</button>
                      </div>
                    ) : (
                      <label htmlFor={inputId} className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#0A2540] hover:text-[#00C4B4] cursor-pointer">
                        <Upload size={14} /> {uploading === kind ? "Uploading…" : `Upload ${def.label}`}
                      </label>
                    )}
                    <input id={inputId} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={uploading === kind} onChange={(e) => uploadDoc(e, kind)} data-testid={`kyc-upload-input-${kind}`} />
                  </div>
                );
              })}
            </div>
            {missingDocs.length > 0 && (
              <p className="mt-3 text-xs text-orange-700" data-testid="kyc-missing-docs">
                Still need: {missingDocs.map((k) => DOC_DEFS[k].label).join(", ")}
              </p>
            )}
          </div>

          <Button
            data-testid="kyc-submit-btn"
            disabled={!canSubmit}
            type="submit"
            className="bg-[#0A2540] hover:bg-[#133D6B] text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Submitting…" : "Submit for review"}
          </Button>
          {!canSubmit && !busy && (
            <p className="text-xs text-[#999999]">
              {missingDocs.length > 0
                ? `Upload the remaining ${missingDocs.length} document(s) to enable submission.`
                : "Complete every required field above to enable submission."}
            </p>
          )}
        </form>
      )}
    </DashboardShell>
  );
}
